/**
 * Wraps any route subtree that requires a real, non-expired session.
 * Redirects to `/login`, preserving the original destination in
 * `location.state.from` so `LoginPage` can send the user back after a
 * successful login — the standard React Router redirect-back pattern.
 *
 * Disaster-recovery exposure: protected routes (e.g. `/administration`)
 * are shared across every environment — only the login screen has a
 * `/recovered` variant (AppRouter.tsx). So a logout/session-expiry
 * redirect must check the persisted environment store, not the current
 * URL, to send a recovery-environment session back to
 * `/recovered/login` rather than dropping it onto the production login
 * screen.
 */
import type { JSX } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@store/auth.store';
import { useEnvironmentStore } from '@store/environment.store';

export function ProtectedRoute(): JSX.Element {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);
  const environment = useEnvironmentStore((state) => state.environment);
  const location = useLocation();

  if (!isAuthenticated || isTokenExpired()) {
    const loginPath = environment === 'recovery' ? '/recovered/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  return <Outlet />;
}
